<template>
  <form-field-v2
    :label="label"
    :hint="hint"
    :error="error"
    :help="help"
    :disabled="disabled"
    :label-for="triggerId"
    strong
  >
    <div ref="root" class="dropdown-v2 relative" @focusout="onFocusOut">
      <button
        :id="triggerId"
        ref="trigger"
        type="button"
        class="dropdown-v2__trigger select-v2 w-full flex flex-row items-center ltr:text-left rtl:text-right"
        :class="{ 'is-error': !!error }"
        :disabled="disabled"
        aria-haspopup="listbox"
        :aria-expanded="isOpen ? 'true' : 'false'"
        @click="toggle"
      >
        <span class="flex-1 min-w-0 truncate">
          {{ triggerText }}
        </span>
      </button>

      <div
        v-if="isOpen"
        ref="panel"
        class="dropdown-v2__panel fixed z-[100] bg-white rounded-[8px] border border-solid border-gray-100 shadow-sm overflow-y-auto"
        :style="panelStyle"
        @keydown="onPanelKeydown"
      >
        <div
          v-if="searchable"
          class="p-[7px] border-b border-solid border-gray-200"
        >
          <div class="relative">
            <span
              class="material-symbols-outlined text-[24px] text-[#6B7280] absolute top-1/2 -translate-y-1/2 ltr:left-3 rtl:right-3 pointer-events-none"
            >
              search
            </span>
            <input
              ref="search"
              v-model="search"
              type="text"
              class="input-v2 w-full !h-10 ltr:pl-[43px] rtl:pr-[43px]"
              :placeholder="searchPlaceholder"
              :aria-controls="listboxId"
              :aria-activedescendant="activeOption ? activeOptionId : null"
            />
          </div>
        </div>

        <div
          v-if="!filteredGroups.length"
          class="px-3 py-3 text-sm leading-5 text-gray-500"
        >
          {{ emptyText }}
        </div>

        <div
          :id="listboxId"
          ref="listbox"
          role="listbox"
          tabindex="-1"
          class="outline-none"
          :aria-multiselectable="multiple ? 'true' : 'false'"
          :aria-activedescendant="activeOption ? activeOptionId : null"
        >
          <div
            v-for="(group, groupIndex) in filteredGroups"
            :key="group.label || groupIndex"
          >
            <div
              v-if="group.label"
              class="h-[42px] px-3 flex flex-row items-center justify-between"
              :class="{ 'cursor-pointer': collapsibleGroups }"
              @click="toggleGroup(groupIndex)"
            >
              <span
                class="text-xs leading-[18px] font-semibold text-gray-400 uppercase"
              >
                {{ group.label }}
              </span>
              <span
                v-if="collapsibleGroups"
                class="material-symbols-outlined text-[24px] text-gray-500"
              >
                {{
                  isGroupOpen(groupIndex)
                    ? 'keyboard_arrow_up'
                    : 'keyboard_arrow_down'
                }}
              </span>
            </div>
            <template v-if="isGroupOpen(groupIndex)">
              <div
                v-for="(option, optionIndex) in group.options"
                :id="option === activeOption ? activeOptionId : null"
                :key="optionKey(option, optionIndex)"
                role="option"
                :aria-selected="isSelected(option.value) ? 'true' : 'false'"
                class="dropdown-v2__option h-11 px-3 flex flex-row items-center justify-between gap-2 text-base text-[#164B35]"
                :class="optionClasses(option)"
                @click="onSelect(option)"
              >
                <span class="truncate">{{ option.label }}</span>
                <span
                  v-if="isSelected(option.value)"
                  class="material-symbols-outlined text-[24px] text-[#1F845A] shrink-0"
                >
                  check
                </span>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </form-field-v2>
</template>

<script>
import FormFieldV2 from './FormFieldV2.vue';

let instanceCount = 0;
const PANEL_GAP = 6;
const VIEWPORT_MARGIN = 8;

const sameValue = (a, b) =>
  a === b ||
  (a !== null &&
    b !== null &&
    typeof a === 'object' &&
    typeof b === 'object' &&
    JSON.stringify(a) === JSON.stringify(b));

export default {
  name: 'DropdownV2',
  components: { FormFieldV2 },
  props: {
    value: { type: [String, Number, Boolean, Object, Array], default: null },
    options: { type: Array, default: () => [] },
    groups: { type: Array, default: () => [] },
    multiple: { type: Boolean, default: false },
    searchable: { type: Boolean, default: false },
    searchPlaceholder: { type: String, default: '' },
    emptyText: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    label: { type: String, default: '' },
    hint: { type: String, default: '' },
    error: { type: String, default: '' },
    help: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    closeOnSelect: { type: Boolean, default: undefined },
    showSelection: { type: Boolean, default: true },
    collapsibleGroups: { type: Boolean, default: true },
    maxHeight: { type: Number, default: 320 },
  },
  data() {
    instanceCount += 1;
    return {
      instanceId: instanceCount,
      isOpen: false,
      search: '',
      openGroupIndex: 0,
      panelStyle: {},
      activeIndex: -1,
    };
  },
  computed: {
    triggerId() {
      return `dropdown-v2-${this.instanceId}`;
    },
    listboxId() {
      return `${this.triggerId}-listbox`;
    },
    activeOptionId() {
      return `${this.triggerId}-active`;
    },
    normalizedGroups() {
      if (this.groups.length) {
        return this.groups.map(group => ({
          label: group.label,
          options: group.options || [],
        }));
      }
      return [{ label: '', options: this.options }];
    },
    searchQuery() {
      return this.search.trim().toLowerCase();
    },
    filteredGroups() {
      return this.normalizedGroups
        .map(group => ({
          ...group,
          options: this.searchQuery
            ? group.options.filter(option =>
                String(option.label || '')
                  .toLowerCase()
                  .includes(this.searchQuery)
              )
            : group.options,
        }))
        .filter(group => group.options.length);
    },
    navigableOptions() {
      return this.filteredGroups
        .filter((group, index) => this.isGroupOpen(index))
        .flatMap(group => group.options)
        .filter(option => !option.disabled);
    },
    activeOption() {
      return this.navigableOptions[this.activeIndex] || null;
    },
    selectedValues() {
      if (this.multiple) {
        return Array.isArray(this.value) ? this.value : [];
      }
      return this.value === null || this.value === undefined
        ? []
        : [this.value];
    },
    selectedLabels() {
      return this.normalizedGroups
        .flatMap(group => group.options)
        .filter(option => this.isSelected(option.value))
        .map(option => option.label);
    },
    hasSelection() {
      return this.showSelection && this.selectedLabels.length > 0;
    },
    triggerText() {
      return this.hasSelection
        ? this.selectedLabels.join(', ')
        : this.placeholder;
    },
    shouldCloseOnSelect() {
      return this.closeOnSelect === undefined
        ? !this.multiple
        : this.closeOnSelect;
    },
  },
  watch: {
    searchQuery(query) {
      this.activeIndex = query ? 0 : -1;
    },
  },
  beforeDestroy() {
    this.removeListeners();
  },
  methods: {
    optionKey(option, index) {
      if (option.key !== undefined) return option.key;
      return typeof option.value === 'object' ? index : option.value;
    },
    optionClasses(option) {
      if (option.disabled) return 'opacity-50 cursor-not-allowed';
      const active = option === this.activeOption;
      const selected = this.isSelected(option.value);
      return [
        'cursor-pointer hover:bg-gray-50',
        {
          'bg-green-50': selected && !active,
          'bg-green-100': selected && active,
          'bg-gray-50': active && !selected,
        },
      ];
    },
    isSelected(value) {
      return this.selectedValues.some(selected => sameValue(selected, value));
    },
    isGroupOpen(index) {
      if (!this.collapsibleGroups || this.searchQuery) return true;
      return this.openGroupIndex === index;
    },
    toggleGroup(index) {
      if (!this.collapsibleGroups) return;
      this.openGroupIndex = this.openGroupIndex === index ? null : index;
    },
    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    },
    open() {
      if (this.disabled || this.isOpen) return;
      this.isOpen = true;
      this.activeIndex = -1;
      this.addListeners();
      this.$emit('open');
      this.$nextTick(() => {
        this.updatePosition();
        (this.searchable ? this.$refs.search : this.$refs.listbox)?.focus();
      });
    },
    close(restoreFocus = true) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.search = '';
      this.activeIndex = -1;
      this.removeListeners();
      this.$emit('close');
      if (restoreFocus) this.$refs.trigger?.focus();
    },
    onSelect(option) {
      if (option.disabled) return;
      this.$emit('select', option.value);
      if (this.multiple) {
        const next = this.isSelected(option.value)
          ? this.selectedValues.filter(v => !sameValue(v, option.value))
          : [...this.selectedValues, option.value];
        this.$emit('input', next);
      } else {
        this.$emit('input', option.value);
      }
      if (this.shouldCloseOnSelect) this.close();
    },
    moveActive(step) {
      const last = this.navigableOptions.length - 1;
      this.activeIndex = Math.min(Math.max(this.activeIndex + step, 0), last);
      this.$nextTick(() =>
        document
          .getElementById(this.activeOptionId)
          ?.scrollIntoView?.({ block: 'nearest' })
      );
    },
    onPanelKeydown(event) {
      const { key } = event;
      if (key === 'ArrowDown' || key === 'ArrowUp') {
        event.preventDefault();
        this.moveActive(key === 'ArrowDown' ? 1 : -1);
        return;
      }
      const toggles =
        key === ' ' && this.multiple && event.target !== this.$refs.search;
      if (key !== 'Enter' && !toggles) return;
      event.preventDefault();
      if (this.activeOption) this.onSelect(this.activeOption);
    },
    updatePosition() {
      const { trigger } = this.$refs;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
      const spaceAbove = rect.top - VIEWPORT_MARGIN;
      const openUpwards =
        spaceBelow < Math.min(this.maxHeight, 200) && spaceAbove > spaceBelow;
      const room = openUpwards ? spaceAbove : spaceBelow;
      const style = {
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        maxHeight: `${Math.max(Math.min(this.maxHeight, room), 120)}px`,
      };
      if (openUpwards) {
        style.bottom = `${window.innerHeight - rect.top + PANEL_GAP}px`;
      } else {
        style.top = `${rect.bottom + PANEL_GAP}px`;
      }
      this.panelStyle = style;
    },
    onOutsideClick(event) {
      const { root } = this.$refs;
      const target = event.target instanceof Node ? event.target : null;
      if (!root || !target || !root.contains(target)) {
        this.close(false);
      }
    },
    onFocusOut(event) {
      const next = event.relatedTarget;
      if (next instanceof Node && !this.$refs.root.contains(next)) {
        this.close(false);
      }
    },
    onScroll(event) {
      if (this.$refs.panel && this.$refs.panel.contains(event.target)) return;
      this.updatePosition();
    },
    onKeydown(event) {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      this.close();
    },
    addListeners() {
      window.addEventListener('mouseup', this.onOutsideClick);
      window.addEventListener('scroll', this.onScroll, true);
      window.addEventListener('resize', this.updatePosition);
      window.addEventListener('keydown', this.onKeydown, true);
    },
    removeListeners() {
      window.removeEventListener('mouseup', this.onOutsideClick);
      window.removeEventListener('scroll', this.onScroll, true);
      window.removeEventListener('resize', this.updatePosition);
      window.removeEventListener('keydown', this.onKeydown, true);
    },
  },
};
</script>
